/* eslint-disable no-console */
/* eslint-disable max-len */
import React, { useCallback, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'react-toastify';
import { uploadDocumentURL } from '../../../../../Creation/Document/DocumentUploadHelper';
import {
	extractFileTypeFromUrl,
	processDocumentUrl
} from '../EmbedHelpers';
import {
	SUPPORTED_DOCUMENT_FORMATS
} from '../types';
import type { EmbedDocumentsProps, DocumentUrlData } from '../types';

export interface EmbedDocumentsRef {
	process: () => Promise<void>;
}

const EmbedDocuments = forwardRef<EmbedDocumentsRef, EmbedDocumentsProps>(({
	url,
	isProcessing,
	onUploadComplete,
	onError,
	companyId
}, ref) =>
{
	/**
	 * Validates document URL
	 */
	const validateDocumentUrl = useCallback((inputUrl: string): boolean =>
	{
		// For documents, validate the file extension more strictly
		const fileExt = extractFileTypeFromUrl(inputUrl);

		if (!fileExt || !SUPPORTED_DOCUMENT_FORMATS.includes(fileExt))
		{
			onError('URL must point to a document with a supported extension (.pdf, .docx, etc.)');

			return false;
		}

		return true;
	}, [ onError ]);

	/**
	 * Handles the upload of a document URL directly to the API
	 */
	const handleDocumentUrlUpload = useCallback(async (
		documentData: {
			url: string;
			title?: string;
			description?: string;
			fileType: string;
			thumbnail?: string;
			platform: string;
		}
	) =>
	{
		try
		{
			// Construct the request payload for the API
			const requestData: DocumentUrlData = {
				url         : documentData.url,
				title       : documentData.title,
				description : documentData.description || '',
				fileType    : documentData.fileType,
				coverImage  : documentData.thumbnail,
				platform    : documentData.platform || 'document_url'
			};

			// Use the imported uploadDocumentURL function
			const response = await uploadDocumentURL(companyId, requestData as DocumentUrlData);

			return response;
		}
		catch (error)
		{
			console.error('Error processing document URL:', error);
			// If there's a more specific error message in the error object, log it
			if (error instanceof Error)
			{
				console.error('Error details:', error.message);

				// If there's a response in the error, log it
				if ((error as any).response)
				{
					console.error('API response error:', (error as any).response);
				}
			}
			throw error;
		}
	}, [ companyId ]);

	/**
	 * Processes and uploads document
	 */
	const processAndUploadDocument = useCallback(async (inputUrl: string): Promise<void> =>
	{
		try
		{
			// 1. Validate the document URL
			if (!validateDocumentUrl(inputUrl))
			{
				return;
			}

			// 2. Process Document URL
			let processedData;

			try
			{
				processedData = processDocumentUrl(inputUrl);

				if (!processedData)
				{
					throw new Error('Failed to process Document URL');
				}
			}
			catch (documentError)
			{
				const errorMessage = documentError instanceof Error ? documentError.message : 'Failed to process document';

				onError(errorMessage);

				return;
			}

			// 3. Handle Document upload
			if (companyId)
			{
				const documentUploadParams = {
					url       : processedData.url,
					title     : processedData.title,
					fileType  : processedData.fileType || 'pdf',
					thumbnail : processedData.thumbnail,
					platform  : processedData.platform || 'document_url'
				};

				// Upload the document directly through our helper - this will make the API call
				const response = await handleDocumentUrlUpload(documentUploadParams);

				// Set a flag to skip the parent callback that would trigger another API call
				processedData.skipParentCallback = true;

				// Handle successful response if needed
				if (response?.skipDuplicate)
				{
					console.log('Document already being processed, skipping duplicate upload');
				}
			}

			// 4. Notify parent of successful processing
			onUploadComplete(processedData);
		}
		catch (uploadError)
		{
			console.error('Error processing document URL:', uploadError);
			const errorMessage = uploadError instanceof Error
				? uploadError.message
				: 'Failed to process document URL. Please try again.';

			onError(errorMessage);
			toast.error('Failed to process document URL. Please try again.');
		}
	}, [ validateDocumentUrl, companyId, handleDocumentUrlUpload, onUploadComplete, onError ]);

	/**
	 * Main processing function called by parent
	 */
	const processDocument = useCallback(async (): Promise<void> =>
	{
		if (isProcessing || !url.trim()) return;

		await processAndUploadDocument(url);
	}, [ url, isProcessing, processAndUploadDocument ]);

	// Expose the process function for parent to call
	useImperativeHandle(ref, () => ({
		process : processDocument
	}));

	// This component doesn't render any UI, it's purely for logic
	// The parent component handles all UI rendering
	return null;
});

EmbedDocuments.displayName = 'EmbedDocuments';

export default EmbedDocuments;